import { createContext, ReactNode, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { match } from 'ts-pattern'

export type NavigationContextValue = {
  /**
   * The direction of the most recent navigation. This will always be updated
   * before the navigation happens, so it can be used in determining which
   * direction to animate the navigation.
   */
  direction: 'forward' | 'backward'
  goBack: () => void
  go: (n: number) => void
  push: (url: string) => void
  goHome: () => void
}

export const NavigationContext = createContext<
  NavigationContextValue | undefined
>(undefined)

type Props = {
  children: ReactNode
}

/**
 * Provides `goBack`, `go`, and `push` for navigating, and the direction of the navigation.
 * The provided functions navigate in a way that ensures the direction is
 * updated before the navigation happens. This allows for animating the
 * navigation based on the direction.
 */
export function NavigationContextProvider(props: Props) {
  const history = useHistory()
  const [navigationIntent, setNavigationIntent] = useState<{
    direction: 'forward' | 'backward'
    url?: string
    // number of pages to move forward when using the `go` function,
    // a negative number represents going backwards
    pagesForward?: number
  }>()

  const [urls, setUrls] = useState<string[]>([location.pathname])

  function push(url: string) {
    setNavigationIntent({
      direction: 'forward',
      url,
    })
  }

  function goBack() {
    // set url to the previous url
    setNavigationIntent({ direction: 'backward' })
  }

  function go(n: number) {
    if (n === 0) return

    const direction = n > 0 ? 'forward' : 'backward'
    setNavigationIntent({ direction, pagesForward: n })
  }

  function goHome() {
    const homeUrl =
      urls.find((url) => {
        return match(url)
          .when(
            (url) =>
              url.match(/^\/app(?:\/[A-Za-z]+\/[0-9]+)?$/g)?.length ?? 0 > 0,
            () => {
              return true
            },
          )
          .when(
            (url) =>
              url.match(/^\/devices(?:\/[A-Za-z]+\/[0-9]+)?$/g)?.length ??
              0 > 0,
            () => {
              return true
            },
          )
          .otherwise(() => {
            return false
          })
      }) ?? '/app'

    setNavigationIntent({
      url: homeUrl,
      direction: 'forward',
    })
  }

  // When a history change happens, navigate after the navigation direction has been set
  useEffect(() => {
    if (navigationIntent?.direction === 'backward') {
      history.go(navigationIntent?.pagesForward ?? -1)
    } else if (navigationIntent?.direction === 'forward') {
      if (navigationIntent?.url) {
        setUrls([navigationIntent.url, ...urls])
        history.push(navigationIntent.url)
      } else if (navigationIntent?.pagesForward) {
        history.go(navigationIntent.pagesForward)
      }
    }
  }, [navigationIntent])

  const value = useMemo(
    () => ({
      direction: navigationIntent?.direction ?? 'forward',
      push,
      goBack,
      goHome,
      go,
    }),
    [navigationIntent],
  )

  return (
    <NavigationContext.Provider value={value}>
      {props.children}
    </NavigationContext.Provider>
  )
}
